// src/components/education/periodic-table/PeriodicTable2D.tsx
// A simple 2D version of the periodic table that doesn't use Three.js
import React from 'react';
import styled from 'styled-components';
import Element, { ElementData } from './Element';
import elementsData from './data/elements.json';

interface PeriodicTableProps {
  onElementSelect?: (element: ElementData) => void;
  highlightedElements?: string[];
}

// Styled components
const TableContainer = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  width: 100%;
  margin: 0 auto;
  padding: 1rem;
`;

const Table = styled.div`
  display: grid;
  grid-template-columns: repeat(18, minmax(60px, 1fr));
  gap: 8px;
  margin: 0 auto;
  width: 100%;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin: 2rem 0;
  color: rgba(255, 255, 255, 0.9);
`;

interface LegendItemProps {
  color: string;
}

const LegendItem = styled.div<LegendItemProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegendColor = styled.div<LegendItemProps>`
  width: 20px;
  height: 20px;
  background-color: ${props => props.color};
  border-radius: 4px;
`;

const LegendText = styled.span`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
`;

// Helper functions
const getGridPosition = (xpos: number, ypos: number) => {
  // La-Lu series (lanthanoids) and Ac-Lr series (actinoids) are displayed below main table
  if (
    (xpos >= 3 && ypos === 6 && xpos <= 17) || 
    (xpos >= 3 && ypos === 7 && xpos <= 17)
  ) {
    return {
      gridColumnStart: xpos + 15 - 17, 
      gridRowStart: ypos + 3
    };
  }
  
  return {
    gridColumnStart: xpos,
    gridRowStart: ypos
  };
};

const categories = [
  { name: 'alkali metal', color: '#F1383E' },
  { name: 'alkaline earth metal', color: '#FF6D00' },
  { name: 'transition metal', color: '#FFC107' },
  { name: 'post-transition metal', color: '#8BC34A' },
  { name: 'metalloid', color: '#4CAF50' },
  { name: 'nonmetal', color: '#009688' },
  { name: 'halogen', color: '#00BCD4' },
  { name: 'noble gas', color: '#5662E9' },
  { name: 'lanthanoid', color: '#AB47BC' },
  { name: 'actinoid', color: '#7E57C2' },
];

const PeriodicTable2D: React.FC<PeriodicTableProps> = ({ 
  onElementSelect, 
  highlightedElements = [] 
}) => {
  const handleElementClick = (element: ElementData) => {
    if (onElementSelect) {
      onElementSelect(element);
    }
  };

  return (
    <TableContainer>
      <Legend>
        {categories.map((category) => (
          <LegendItem key={category.name} color={category.color}>
            <LegendColor color={category.color} />
            <LegendText>{category.name}</LegendText>
          </LegendItem>
        ))}
      </Legend>
      
      <Table>
        {(elementsData as ElementData[]).map((element) => {
          const position = getGridPosition(element.xpos, element.ypos);
          const isHighlighted = highlightedElements.includes(element.symbol);
          
          return (
            <div
              key={element.number}
              style={{
                gridColumnStart: position.gridColumnStart,
                gridRowStart: position.gridRowStart,
              }}
            >
              <Element
                data={element}
                isHighlighted={isHighlighted}
                onClick={() => handleElementClick(element)}
              />
            </div>
          );
        })}
      </Table>
    </TableContainer>
  );
};

export default PeriodicTable2D; 